import { createContext, use, useMemo, useState, type ComponentProps, type PropsWithChildren } from "react";

import { CrossIcon } from "@eisox/icons";
import type { ChartColorLegend, ChartTooltipContent, ChartValue } from "@tanstack/charts";
import { controlledSignal } from "@tanstack/charts/interaction/signal";
import { interactiveColorLegend } from "@tanstack/charts/legend";
import { motion } from "@tanstack/charts/motion";
import { RendererChart, type RendererChartProps } from "@tanstack/charts/react/tooltip";

import { cn } from "..";

/* -------------------------------------------------------------------------------------------------
 * Context
 * -----------------------------------------------------------------------------------------------*/

interface ChartContextValue {
  series: { key: string; color: string }[];
  hiddenSeries: string[];
  setHiddenSeries: (hiddenSeries: string[]) => void;
}

const ChartContext = createContext<ChartContextValue | null>(null);

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

const DefaultTooltipBody = ({ content }: { content: string | ChartTooltipContent }) =>
  typeof content === "string" ? (
    content
  ) : (
    <div className="gap-y-2.5 flex flex-col">
      {content.title && <p className="font-medium text-primary-dark-gray">{content.title}</p>}
      {content.rows.map((row, index) => (
        <div key={`${row.label}-${index}`} className="gap-y-1.25 flex flex-col">
          {row.label && (
            <div className="gap-x-1.25 font-normal flex items-center">
              {row.color && <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: row.color }} />}
              {row.label}
            </div>
          )}
          <span className="font-medium tabular-nums">{row.value}</span>
        </div>
      ))}
    </div>
  );

const renderer = motion({ initial: "always", resize: true, transition: { type: "tween", duration: 600 } });

const THEME_CLASS_NAME =
  "min-w-0 gap-2.5 flex flex-col [font-family:var(--font-family)] text-sm text-primary-dark-blue [--ts-chart-1:var(--chart-blue-color)] [--ts-chart-2:var(--chart-green-color)] [--ts-chart-3:var(--chart-indigo-color)] [--ts-chart-4:var(--chart-yellow-color)] [--ts-chart-5:var(--chart-purple-color)] [--ts-chart-6:var(--chart-teal-color)] [--ts-chart-7:var(--chart-orange-color)] [--ts-chart-8:var(--chart-sky-color)] [--ts-chart-9:var(--chart-olive-color)] [--ts-chart-10:var(--chart-rose-color)] [--ts-chart-tooltip-background:var(--primary-dark-blue-color)] [--ts-chart-tooltip-color:var(--primary-white-color)] [--ts-chart-tooltip-border-radius:10px] [--ts-chart-tooltip-border:0] [--ts-chart-tooltip-shadow:none] [--ts-chart-tooltip-max-width:calc(var(--spacing)*80)] [--ts-chart-tooltip-font:400_var(--font-size-base)/1.4_var(--font-family)] motion-safe:[&_g[class$=line]]:animate-chart-draw [--ts-chart-tooltip-padding:calc(var(--spacing)*5)] [&_[role=img]]:outline-none";

type Definition<TDatum, TXValue extends ChartValue, TYValue extends ChartValue> = RendererChartProps<
  TDatum,
  TXValue,
  TYValue
>["definition"];

type RootProps<
  TDatum = unknown,
  TXValue extends ChartValue = ChartValue,
  TYValue extends ChartValue = ChartValue,
> = PropsWithChildren<
  Omit<RendererChartProps<TDatum, TXValue, TYValue>, "renderer" | "definition"> & {
    definition:
      | Definition<TDatum, TXValue, TYValue>
      | ((hiddenSeries: readonly string[]) => Definition<TDatum, TXValue, TYValue>);
    defaultHiddenSeries?: readonly string[];
  }
>;

const Root = <TDatum, TXValue extends ChartValue = ChartValue, TYValue extends ChartValue = ChartValue>({
  className,
  style,
  children,
  definition,
  defaultHiddenSeries = [],
  onRender,
  renderTooltipBody,
  ...props
}: RootProps<TDatum, TXValue, TYValue>) => {
  const [series, setSeries] = useState<ChartContextValue["series"]>([]);
  const [hiddenSeries, setHiddenSeries] = useState(() => [...defaultHiddenSeries]);
  const fillsParent = props.height === undefined && props.aspectRatio === undefined;

  const filteredDefinition = useMemo(() => {
    const visibleDefinition = typeof definition === "function" ? definition(hiddenSeries) : definition;
    if (hiddenSeries.length === 0) return visibleDefinition;

    const legend: ChartColorLegend = {
      ...interactiveColorLegend({
        visible: controlledSignal<readonly string[]>(
          series.map(({ key }) => key).filter(key => !hiddenSeries.includes(key)),
          () => undefined,
        ),
      }),
      height: () => 0,
      render: () => ({ kind: "group", key: "legend", children: [] }),
      control: undefined,
    };

    if (!("chart" in visibleDefinition)) return { ...visibleDefinition, color: { ...visibleDefinition.color, legend } };

    return {
      ...visibleDefinition,
      chart: (context: Parameters<typeof visibleDefinition.chart>[0]) => {
        const spec = visibleDefinition.chart(context);
        return { ...spec, color: { ...spec.color, legend } };
      },
    };
  }, [definition, hiddenSeries, series]);

  return (
    <ChartContext value={{ series, hiddenSeries, setHiddenSeries }}>
      <div className={cn(THEME_CLASS_NAME, fillsParent && "h-full", className)} style={style}>
        <RendererChart
          {...props}
          renderer={renderer}
          definition={filteredDefinition}
          className={fillsParent ? "min-h-0 flex-1" : undefined}
          style={fillsParent ? { height: undefined } : undefined}
          renderTooltipBody={tooltipBody => (
            <div className="min-w-45 max-w-(--ts-chart-tooltip-max-width) rounded-(--ts-chart-tooltip-border-radius) bg-(--ts-chart-tooltip-background) p-(--ts-chart-tooltip-padding) text-(--ts-chart-tooltip-color) shadow-(--ts-chart-tooltip-shadow) [border:var(--ts-chart-tooltip-border)] [font:var(--ts-chart-tooltip-font)]">
              {renderTooltipBody ? (
                renderTooltipBody(tooltipBody)
              ) : (
                <DefaultTooltipBody content={tooltipBody.content} />
              )}
            </div>
          )}
          onRender={context => {
            onRender?.(context);
            const { domain, map } = context.scene.colors;
            const nextSeries = domain.map(key => ({ key: String(key), color: map(key) }));
            if (JSON.stringify(nextSeries) !== JSON.stringify(series)) setSeries(nextSeries);
          }}
        />
        {children}
      </div>
    </ChartContext>
  );
};

Root.displayName = "ChartRoot";

/* -------------------------------------------------------------------------------------------------
 * Legend
 * -----------------------------------------------------------------------------------------------*/

type LegendProps = Omit<ComponentProps<"ul">, "children"> & {
  label?: (series: string) => string;
  onRemove?: (series: string) => void;
  removeLabel?: (label: string) => string;
};

const Legend = ({ className, label, onRemove, removeLabel, ...props }: LegendProps) => {
  const context = use(ChartContext);
  if (!context) throw new Error("Chart.Legend must be used within Chart.Root");
  const { series, hiddenSeries, setHiddenSeries } = context;

  return (
    <ul
      className={cn("gap-x-5 gap-y-1.5 grid grid-cols-[repeat(auto-fit,minmax(0,12rem))] justify-center", className)}
      {...props}
    >
      {series.map(({ key, color }) => {
        const hidden = hiddenSeries.includes(key);
        const seriesLabel = label?.(key) ?? key;

        return (
          <li key={key} className="gap-1.5 flex items-center">
            <button
              type="button"
              aria-pressed={!hidden}
              className={cn("gap-1.5 flex cursor-pointer items-center text-left", hidden && "opacity-40")}
              onClick={() =>
                setHiddenSeries(hidden ? hiddenSeries.filter(series => series !== key) : [...hiddenSeries, key])
              }
            >
              <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
              {seriesLabel}
            </button>
            {onRemove && (
              <button
                type="button"
                aria-label={removeLabel?.(seriesLabel)}
                className="cursor-pointer"
                onClick={() => onRemove(key)}
              >
                <CrossIcon className="size-2.5" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

Legend.displayName = "ChartLegend";

export { Legend, Root };
export type { LegendProps, RootProps };
